// Hub ▸ Marketing ▸ Email ▸ Results (slice S5, CEO 2026-10-22; review 1): what
// each e-mail brought in, from the Klaviyo numbers saved every night and Flow's
// own leads and orders (GET /api/marketing/email/results).
//   1. the answer first — the top earner (revenue; gross profit for admin +
//      finance — marketing-only viewers get no profit field at all, CEO
//      2026-10-22) and how many campaigns brought no order — then the volume;
//   2. ONE deliverability verdict for the page (icon + words: healthy / watch /
//      pause, the last full week, which recent weeks crossed a line);
//   3. campaigns sent in the dates, highest revenue first, the top earner
//      marked in words; footer = these campaigns only; then everything e-mail
//      (campaigns + flows) on its own line; bounces / spam / delivered behind
//      "More columns"; CSV; cards on phones;
//   4. flows; 5. deliverability by week (weeks under 1,000 e-mails: "small week").
// Every column has its definition (ⓘ). Nothing is sent from here.
import { useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, Download, HelpCircle, PauseCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import {
  EmptyState, ErrorLine, InfoTip, Num, Panel, PeriodPicker, Pill, Skeleton, SortTable, count, downloadText, money, periodQuery, shortDay, toCsv, useApi,
  type Column, type PeriodState,
} from "./kit";
import type { DeliverabilityFlag, EmailResultRow, EmailResultsReady, EmailResultsResponse, EmailTotals } from "./types";

const pct = (v: number | null | undefined, digits = 1) => (v == null ? "—" : `${(v * 100).toFixed(v !== 0 && Math.abs(v) < 0.01 ? Math.max(digits, 2) : digits)}%`);
const FLAG_WORDS: Record<DeliverabilityFlag, { tone: "warn" | "risk"; words: string }> = {
  spam_pause: { tone: "risk", words: "Spam past 0.3% — hold sends" },
  spam: { tone: "warn", words: "Spam over 0.1%" },
  bounce: { tone: "warn", words: "Bounces over 2%" },
  opens: { tone: "warn", words: "Opens under 20%" },
  unsubs: { tone: "warn", words: "Unsubs over 0.5%" },
};
type Row = EmailResultRow | (EmailTotals & { id: string; kind: "campaign"; name: string; sentAt: null; audience: string; topEarner: false });

function columns(f: Record<string, string>, gpHidden: boolean, ga4: boolean, kind: "campaign" | "flow", more: boolean): Column<Row>[] {
  const n = (key: string, label: string, fk: string, get: (r: Row) => number | null, show: (r: Row) => React.ReactNode): Column<Row> => ({ key, label, numeric: true, info: f[fk], value: get, render: (r) => <Num v={get(r)}>{show(r)}</Num> });
  return [
    {
      key: "name", label: kind === "campaign" ? "Campaign" : "Flow", value: (r) => r.name,
      render: (r) => (
        <span>
          <span className="font-semibold text-ink">{r.name}</span>
          {r.topEarner && <span className="ml-1.5 inline-block rounded border border-ink px-1.5 text-meta font-semibold uppercase tracking-[0.06em] text-ink">Top earner</span>}
          {kind === "campaign" && r.id !== "total" && <span className="block text-meta text-ink-muted">{r.sentAt ? `Sent ${shortDay(r.sentAt)}` : "Sent"}{r.audience ? ` · to ${r.audience}` : ""}</span>}
        </span>
      ),
    },
    // Unsubscribes under "Sent", opens beside clicks, orders under revenue — so the table fits a 1280 screen.
    n("sent", "Sent", "sent", (r) => r.recipients, (r) => <span className="block">{count(r.recipients)}{r.unsubscribes !== null && <span className="block text-meta font-normal text-ink-muted">{count(r.unsubscribes)} unsubscribed</span>}</span>),
    { key: "click", label: "Open % · click %", numeric: true, info: `${f.open} ${f.click} Sorted by clicks.`, value: (r) => r.clickRate, render: (r) => <span className="whitespace-nowrap">{pct(r.openRate)} · <span className="font-semibold">{pct(r.clickRate)}</span></span> },
    ...(more ? [
      n("delivered", "Delivered", "sent", (r) => r.delivered, (r) => count(r.delivered)),
      n("bounces", "Bounces", "bounces", (r) => r.bounceRate, (r) => <span className="block">{pct(r.bounceRate, 2)}<span className="block text-meta font-normal text-ink-muted">{count(r.bounces)}</span></span>),
      n("spam", "Spam", "spam", (r) => r.spamRate, (r) => <span className="block">{pct(r.spamRate, 2)}<span className="block text-meta font-normal text-ink-muted">{count(r.spamComplaints)}</span></span>),
    ] : []),
    ...(ga4 ? [n("visits", "Website visits", "visits", (r) => r.visits, (r) => count(r.visits))] : []),
    n("leads", "Quote requests", "leads", (r) => r.leads, (r) => count(r.leads)),
    { key: "revenue", label: "Paid orders · revenue", numeric: true, info: `${f.orders} ${f.revenue}`, value: (r) => r.revenueCents, render: (r) => <Num v={r.orders}><span className="block">{money(r.revenueCents)}<span className="block text-meta font-normal text-ink-muted">{count(r.orders)} paid order{r.orders === 1 ? "" : "s"}</span></span></Num> },
    ...(gpHidden ? [] : [n("gp", "Gross profit", "gp", (r) => r.gpCents ?? null, (r) => `${money(r.gpCents ?? null)}${r.uncostedOrders ? "*" : ""}`)]),
  ];
}

/** Phones: each e-mail as a card. */
function Cards({ rows, gpHidden, ga4 }: { rows: EmailResultRow[]; gpHidden: boolean; ga4: boolean }) {
  return (
    <ul className="space-y-2 sm:hidden">
      {rows.map((r) => (
        <li key={r.id} className={cn("rounded-md border p-3", r.topEarner ? "border-2 border-ink" : "border-rule")}>
          <p className="font-semibold text-ink">{r.name}{r.topEarner && <span className="ml-1.5 text-meta font-semibold uppercase tracking-[0.06em]">· Top earner</span>}</p>
          {r.kind === "campaign" && <p className="text-meta text-ink-muted">{r.sentAt ? `Sent ${shortDay(r.sentAt)}` : "Sent"}{r.audience ? ` · to ${r.audience}` : ""}</p>}
          <dl className="mt-2 grid grid-cols-3 gap-2 text-label">
            {([["Revenue", money(r.revenueCents)], ...(gpHidden ? [] : [["Gross profit", money(r.gpCents ?? null)]]), ["Paid orders", count(r.orders)], ["Requests", count(r.leads)], ...(ga4 ? [["Visits", count(r.visits)]] : []), ["Sent", count(r.recipients)], ["Open %", pct(r.openRate)], ["Click %", pct(r.clickRate)], ["Unsubscribes", count(r.unsubscribes)]] as Array<[string, string]>).map(([k, v]) => (
              <div key={k}><dt className="text-meta text-ink-muted">{k}</dt><dd className="tabular-nums">{v}</dd></div>
            ))}
          </dl>
        </li>
      ))}
    </ul>
  );
}

const totalRow = (t: EmailTotals, name: string): Row => ({ ...t, id: "total", kind: "campaign", name, sentAt: null, audience: "", topEarner: false });
function Footer({ cols, t, label }: { cols: Column<Row>[]; t: EmailTotals; label: string }) {
  const r = totalRow(t, label);
  return (
    <tfoot><tr className="border-t-2 border-ink bg-white">{cols.map((c, i) => (
      <td key={c.key} className={cn("px-3 py-3 font-semibold first:pl-4 last:pr-4 sm:first:pl-6 sm:last:pr-6", c.numeric && "text-right tabular-nums")}>{i === 0 ? label : c.render ? c.render(r) : null}</td>
    ))}</tr></tfoot>
  );
}

function exportCsv(d: EmailResultsReady) {
  const gpHidden = d.measure === "revenue";
  const head = ["kind", "name", "sent_at", "audience", "sent", "delivered", "open_rate", "click_rate", "bounces", "spam_complaints", "unsubscribes", "website_visits", "quote_requests", "paid_orders", "revenue", ...(gpHidden ? [] : ["gross_profit", "orders_without_costs"])];
  const usd = (c: number | null | undefined) => (c == null ? "" : (c / 100).toFixed(2));
  const rate = (v: number | null) => (v == null ? "" : v.toFixed(4));
  const rows = [...d.campaigns, ...d.flows].map((r) => [r.kind, r.name, r.sentAt?.slice(0, 10) ?? "", r.audience, r.recipients ?? "", r.delivered ?? "", rate(r.openRate), rate(r.clickRate), r.bounces ?? "", r.spamComplaints ?? "", r.unsubscribes ?? "", r.visits ?? "", r.leads, r.orders, usd(r.revenueCents), ...(gpHidden ? [] : [usd(r.gpCents), r.uncostedOrders ?? 0])]);
  downloadText(`email-results-${d.period.from}-to-${d.period.to}.csv`, toCsv(head, rows));
}

/** ONE deliverability verdict for the page, with an icon AND words (never colour alone). */
export function DeliverabilityLine({ d }: { d: EmailResultsReady }) {
  const s = d.deliverability;
  const Icon = s.level === "healthy" ? CheckCircle2 : s.level === "pause" ? PauseCircle : s.level === "watch" ? AlertTriangle : HelpCircle;
  const tone = s.level === "healthy" ? "border-ok/40 bg-ok-bg text-ok" : s.level === "pause" ? "border-risk/40 bg-risk-bg text-risk" : s.level === "watch" ? "border-warn/40 bg-warn-bg text-warn" : "border-rule bg-paper text-ink";
  return (
    <p role="status" className={cn("flex items-start gap-2 rounded-md border px-4 py-3 text-label", tone)}>
      <Icon className="mt-0.5 h-5 w-5 shrink-0" aria-hidden />
      <span className="text-ink">{s.text}</span>
    </p>
  );
}

function Deliverability({ d }: { d: EmailResultsReady }) {
  if (!d.trend.length) return null;
  const flags = (w: EmailResultsReady["trend"][number]) => (w.small ? <span className="text-ink-muted">small week — not judged</span>
    : w.flags.length ? <span className="flex flex-wrap gap-1">{w.flags.map((x) => <Pill key={x} tone={FLAG_WORDS[x].tone}>{FLAG_WORDS[x].words}</Pill>)}</span> : <span className="text-ink-muted">All clear</span>);
  return (
    <div className="space-y-3">
      <p className="text-meta text-ink-muted">Lines: bounces 2% · spam 0.1% (spam pause line: 0.3%) · opens 20% · unsubscribes 0.5%. Weeks with under 1,000 e-mails are too small to judge.</p>
      <div className="hidden sm:block">
        <SortTable caption="Deliverability by week" rows={d.trend} rowKey={(w) => w.week} initialSort={{ key: "week", dir: "desc" }} minWidth={760}
          columns={[
            { key: "week", label: "Week of", value: (w) => w.week, render: (w) => <span className="whitespace-nowrap font-semibold">{shortDay(w.week)}</span> },
            { key: "flags", label: "Watch", value: (w) => (w.small ? -1 : w.flags.length), render: flags },
            { key: "sent", label: "Sent", numeric: true, value: (w) => w.recipients, render: (w) => count(w.recipients) },
            { key: "open", label: "Open %", numeric: true, value: (w) => w.openRate, render: (w) => pct(w.openRate) },
            { key: "bounce", label: "Bounces", numeric: true, info: "Bounced ÷ sent. Klaviyo stops sending to hard bounces.", value: (w) => w.bounceRate, render: (w) => pct(w.bounceRate, 2) },
            { key: "spam", label: "Spam", numeric: true, info: "Spam complaints ÷ delivered.", value: (w) => w.spamRate, render: (w) => pct(w.spamRate, 2) },
            { key: "unsub", label: "Unsubscribes", numeric: true, info: "Unsubscribes ÷ delivered.", value: (w) => w.unsubRate, render: (w) => pct(w.unsubRate, 2) },
          ]} />
      </div>
      <ul className="space-y-2 sm:hidden">
        {[...d.trend].reverse().map((w) => (
          <li key={w.week} className="rounded-md border border-rule p-3 text-label">
            <p className="font-semibold">Week of {shortDay(w.week)} · {count(w.recipients)} sent</p>
            <p className="text-ink-muted">Opens {pct(w.openRate)} · bounces {pct(w.bounceRate, 2)} · spam {pct(w.spamRate, 2)} · unsubscribes {pct(w.unsubRate, 2)}</p>
            <div className="mt-1">{flags(w)}</div>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** `onReady(true)` once the saved results are switched on — the Email tab then drops its older live tiles and banner (one verdict on the page). */
export function EmailResults({ onReady }: { onReady?: (ready: boolean) => void }) {
  const [period, setPeriod] = useState<PeriodState>(() => { const t = new Date().toISOString().slice(0, 10); return { preset: "ytd", from: `${t.slice(0, 4)}-01-01`, to: t }; });
  const [more, setMore] = useState(false);
  const res = useApi<EmailResultsResponse>(`/api/marketing/email/results?${periodQuery(period)}`);
  const d = res.data && res.data.ready ? res.data : null;
  useEffect(() => { if (res.data) onReady?.(res.data.ready); }, [res.data, onReady]);
  const gpHidden = d?.measure === "revenue";
  const campCols = d ? columns(d.formulas, gpHidden, d.ga4, "campaign", more) : [];
  const flowCols = d ? columns(d.formulas, gpHidden, d.ga4, "flow", more) : [];
  const t = d?.totals;
  return (
    <div className="space-y-6" id="email-results">
      <Panel title="Results" info={<InfoTip label="results" formula={d?.formulas.match ?? "What each e-mail brought in: Klaviyo's numbers, saved every night, beside Flow's own quote requests and paid orders."} />}
        caption={d?.savedAt ? `Klaviyo numbers saved ${new Date(d.savedAt).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" })} · leads and orders from Flow` : "What each e-mail brought in — Klaviyo's numbers beside Flow's own leads and orders"}
        actions={d && <button type="button" onClick={() => exportCsv(d)} className="inline-flex h-10 items-center gap-1.5 rounded-md border border-rule px-3 text-label hover:bg-paper"><Download className="h-4 w-4" aria-hidden />Export CSV</button>}>
        <div className="space-y-5">
          <PeriodPicker value={period} onChange={setPeriod} resolved={d?.period} />
          {res.error && <ErrorLine onRetry={res.reload}>{res.error}</ErrorLine>}
          {res.loading && !res.data && <Skeleton rows={1} label="Loading e-mail results" />}
          {res.data && !res.data.ready && <EmptyState title="E-mail results aren't switched on yet">{res.data.detail}</EmptyState>}
          {d && (
            <div className={cn("space-y-5 transition-opacity", res.loading && "opacity-60")}>
              <div className="max-w-4xl space-y-1.5">
                <p className="text-[22px] font-semibold leading-snug text-ink">{d.sentence}</p>
                {d.detail && <p className="text-body text-ink-muted">{d.detail}</p>}
              </div>
              <DeliverabilityLine d={d} />
              {d.ga4Setup.caption && <p className="text-label text-warn">{d.ga4Setup.caption}</p>}
              <p className="text-meta text-ink-muted">Credit: a quote request counts for the e-mail of the visit just before it (else the visit that first brought them). <Link to="/marketing/channels?view=website" className="font-semibold underline underline-offset-4">Channels ▸ Website</Link> counts by the first visit instead.</p>
              {!d.campaigns.length ? (
                <EmptyState title={d.savedAt ? "No campaigns sent in these dates" : "Nothing saved from Klaviyo yet"}>{d.savedAt ? "Pick other dates, or check the flows below." : "The first nightly import fills this in — campaigns, flows and deliverability."}</EmptyState>
              ) : (
                <>
                  <div className="hidden justify-end sm:flex">
                    <button type="button" aria-pressed={more} onClick={() => setMore((m) => !m)} className="h-9 rounded-md border border-rule px-3 text-label hover:bg-paper">{more ? "Fewer columns" : "More columns (delivered, bounces, spam)"}</button>
                  </div>
                  <div className="hidden sm:block">
                    <SortTable caption="E-mail campaigns sent in these dates" rows={d.campaigns as Row[]} columns={campCols} rowKey={(r) => r.id} initialSort={{ key: "revenue", dir: "desc" }} minWidth={more ? 1100 : d.ga4 ? 860 : 760}
                      rowClass={(r) => (r.topEarner ? "[&>td:first-child]:border-l-4 [&>td:first-child]:border-l-ink" : undefined)}
                      footer={<Footer cols={campCols} t={d.campaignTotals} label="Campaigns only" />} />
                  </div>
                  <Cards rows={d.campaigns} gpHidden={gpHidden} ga4={d.ga4} />
                </>
              )}
              {t && (d.flows.length > 0 || d.campaigns.length > 0) && (
                <p className="rounded-md bg-paper px-4 py-3 text-label text-ink">
                  <span className="font-semibold">Everything e-mail (campaigns + flows):</span> {count(t.recipients)} e-mails · {pct(t.clickRate)} clicked{d.ga4 ? ` · ${count(t.visits)} website visits` : ""} · {count(t.leads)} quote requests · {count(t.orders)} paid orders · {money(t.revenueCents)} revenue{t.gpCents != null ? ` · ${money(t.gpCents)} gross profit` : ""}
                </p>
              )}
              <div className="space-y-1 text-meta text-ink-muted">
                {d.notes.map((n) => <p key={n}>{n}</p>)}
                {!gpHidden && (d.totals.uncostedOrders ?? 0) > 0 && <p>* some orders are left out of gross profit until their costs are entered.</p>}
              </div>
            </div>
          )}
        </div>
      </Panel>

      {d && d.flows.length > 0 && (
        <Panel title="Flows (automated e-mails)" caption="Welcome, win-back and other automated series — their numbers and what came from them in these dates.">
          <div className="hidden sm:block">
            <SortTable caption="Flows in these dates" rows={d.flows as Row[]} columns={flowCols} rowKey={(r) => r.id} initialSort={{ key: "revenue", dir: "desc" }} minWidth={more ? 1100 : d.ga4 ? 860 : 760} />
          </div>
          <Cards rows={d.flows} gpHidden={gpHidden} ga4={d.ga4} />
        </Panel>
      )}

      {d && d.trend.length > 0 && (
        <Panel title="Deliverability by week" info={<InfoTip label="deliverability" formula="Campaigns by the week they were sent, flows by the day: bounces ÷ sent, spam complaints, unsubscribes and opens ÷ delivered — against the warm-up plan's lines." />}>
          <Deliverability d={d} />
        </Panel>
      )}
    </div>
  );
}
